import { memo, useCallback } from 'react';
import { Box, IconButton, TableBody, TableRow } from '@mui/material';
import CustomTableCell from 'components/cmn/CustomTableCell';
import { useProject } from 'hooks/useProject';
import { ICmnTableBodyProps } from 'interface/cmn/commonInfo';
import { IContent } from 'interface/project/projectInfo';
import { getComparatorAny, stableSort } from 'libs/mindUtils';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import uniqueId from 'lodash/uniqueId';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAppDispatch } from 'store';
import { handleChangeMenu } from 'store/menu/menuSlice';
import ProjectStateBox from 'components/project/list/ProjectStateBox';
import AccountProjectListTableBodySkeleton from './AccountProjectListTableBodySkeleton';

interface IAccountProjectListTableBodyProps extends ICmnTableBodyProps<IContent> {
  type: 'admin' | 'manager';
}
function AccountProjectListTableBody({
  loading,
  rows,
  duration,
  type,
}: IAccountProjectListTableBodyProps): JSX.Element {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { getCommCodes } = useProject();
  const { data: commCodesData } = getCommCodes();
  const handleFindInfoByCodeValue = useCallback(
    (codeValue: string): string => {
      return commCodesData?.find(code => code.codeValue === codeValue)?.codeName || '-';
    },
    [commCodesData],
  );
  const handleUrl = {
    admin: '/admin/project/management',
    manager: '/manager/project/management',
  };
  const handleProjectManage = (event: React.MouseEvent<HTMLButtonElement>, _rowInfo: IContent) => {
    event.preventDefault();
    const tmpNavigate = handleUrl[type];
    navigate(tmpNavigate, {
      state: {
        from: location.pathname || '/admin/member-management',
        codeValue: _rowInfo.projectType,
        rowInfo: _rowInfo,
      },
    });
    dispatch(handleChangeMenu({ changeMenu: ['project'] }));
  };
  return (
    <>
      {loading && duration >= 0.5 && <AccountProjectListTableBodySkeleton />}
      {!loading && rows.length === 0 && (
        <TableBody>
          <TableRow>
            <CustomTableCell align="center" colSpan={10} sx={{ border: 'none' }}>
              조회된 데이터가 없습니다.
            </CustomTableCell>
          </TableRow>
        </TableBody>
      )}
      {!loading && rows.length !== 0 && (
        <TableBody>
          {stableSort(rows, getComparatorAny('desc', 'id')).map((row, index) => {
            const isItemSelected = false;
            const labelId = `table-checkbox-${index}`;

            return (
              <TableRow
                hover
                role="checkbox"
                aria-checked={isItemSelected}
                tabIndex={-1}
                key={`${row.id}-${uniqueId()}`}
                selected={isItemSelected}
                id={labelId}
              >
                <CustomTableCell align="center" sx={{ width: 120, maxWidth: 120 }}>
                  <ProjectStateBox projectState={handleFindInfoByCodeValue(row.projectState)} />
                </CustomTableCell>
                <CustomTableCell align="center">{handleFindInfoByCodeValue(row.projectType)}</CustomTableCell>
                <CustomTableCell align="center" sx={{ width: 120, maxWidth: 120 }}>
                  <Box
                    sx={{
                      whiteSpace: 'nowrap',
                      textOverflow: 'ellipsis',
                      overflow: 'hidden',
                      display: 'block',
                      width: '100%',
                      textAlign: 'center',
                    }}
                  >
                    {row.projectAdmins.map(obj => obj.name).join(', ')}
                  </Box>
                </CustomTableCell>
                <CustomTableCell align="center">{row.compName}</CustomTableCell>
                <CustomTableCell align="center">{row.projectStartAt}</CustomTableCell>
                <CustomTableCell align="center">{row.projectEndAt}</CustomTableCell>
                <CustomTableCell align="center">{row.projectStatus.progress || 0}%</CustomTableCell>
                <CustomTableCell align="center">
                  <IconButton onClick={e => handleProjectManage(e, row)}>
                    <SettingsOutlinedIcon />
                  </IconButton>
                </CustomTableCell>
              </TableRow>
            );
          })}
        </TableBody>
      )}
    </>
  );
}

export default memo(AccountProjectListTableBody);
